import { minAndMaxOf } from "@@/utilities/min-max-of";
import { sortBy } from "@@/utilities/sort";
import { uniqueBy } from "@@/utilities/unique";
import type { Except } from "type-fest";
import { z } from "zod";
import { percentageZodSchema } from "../percentage";
import type { Price} from "../prices/price";
import { priceForHumansFactory, priceZodSchema } from "../prices/price";
import { translatableStringFactory } from "../translations/translation";

const giftCardPriceSettingsZodObject = z
    .object({
        /**
         * When enabled pricing is enabled to be selected completely free by writing their
         * own amount, if disabled the customer can only select from the available options
         */
        freePricingEnabled: z.boolean(),
        /** The default value to auto-pick for user  (in kr as inputted, not öre/cent e.t.c.) */
        defaultAmountInclVat: z.number().int().min(0).optional(),
        /** If free pricing is enabled, this is the minimal value that can be added (in kr as inputted, not öre/cent e.t.c.) */
        minAmountInclVat: z.number().int().min(1).optional(),
        /** If free pricing is enabled, this is the maximal value that can be added (in kr as inputted, not öre/cent e.t.c.) */
        maxAmountInclVat: z.number().int().min(1).optional(),
        /** The VAT percentage */
        vat: percentageZodSchema,
        /** When entering a min and max value, setting the step value generates options for the customer to choose from for every x amount between min and max  */
        // optionAmountInclVatStep: z.number().int().min(20).optional(),
        /** The available price options for customer to select from */
        options: z.array(priceZodSchema).min(1),
    })
    .refine(
        data =>
            !data.freePricingEnabled
                ? true
                : typeof data.minAmountInclVat !== "undefined" &&
                  typeof data.maxAmountInclVat !== "undefined",
        translatableStringFactory({
            sv: "Om fri prissättning är aktiverad måste min- och maxbelopp angetts",
            en: "If free pricing is enabled, min and max amounts must be set",
        }),
    );

type GiftCardPriceSettings = z.infer<typeof giftCardPriceSettingsZodObject>;
const giftCardPriceSettingsFactory = (
    params: Except<GiftCardPriceSettings, "options"> & {
        options: number[];
    },
): GiftCardPriceSettings => {
    const freePricingEnabled = params.freePricingEnabled;
    const minAmountInclVat = params.minAmountInclVat ?? 0;
    const maxAmountInclVat = params.maxAmountInclVat ?? 0;
    const vat = params.vat;
    // Find the min and max of the min and max values, in case they were swapped
    const { min, max } = minAndMaxOf([minAmountInclVat, maxAmountInclVat]);

    // If there are manually added options available
    // Add them to the options array
    const options: Price[] = params.options.map(amountInclVat =>
        priceForHumansFactory({
            amountIncludingVat: amountInclVat,
            vat: vat,
        }),
    );

    // Calculate additional options based on the step value
    // If the step value is set and min and max values are available
    // if (params.optionAmountInclVatStep && min && max) {
    //     for (let i = min; i <= max; i += params.optionAmountInclVatStep) {
    //         const price = priceForHumansFactory({
    //             amountIncludingVat: i,
    //             vat: vat,
    //         });
    //         options.push(price);
    //     }
    // }

    // Sort the options by amountIncludingVat
    options.sort(sortBy(price => price.amountIncludingVat));

    // Remove duplicates
    const uniqueOptions = uniqueBy(options, price => price.amountIncludingVat);
    // eslint-disable-next-line no-console
    console.log("ON FACTORY", params);
    return {
        freePricingEnabled,
        minAmountInclVat: min,
        maxAmountInclVat: max,
        // optionAmountInclVatStep: params.optionAmountInclVatStep,
        options: uniqueOptions,
        // When `freePricingEnabled` is `true` `priceAmountInclVat` will be set to the value of `minAmountInclVat`
        // to be used as a default price for the gift card where the app requires a products price to be shown
        defaultAmountInclVat: params.defaultAmountInclVat,
        vat,
    };
};

export { giftCardPriceSettingsFactory, giftCardPriceSettingsZodObject };
export type { GiftCardPriceSettings };
